import Link from "next/link";
import { Fragment } from "react";
import { FaInstagram, FaTiktok, FaYoutube } from "react-icons/fa6";
import { GetProductQuery, Guide } from "../../../../../graphql/operations";
import { useAnalyticsTracker } from "../../../../Atoms/Analytics";
import { CloudinaryImage } from "../../../../Atoms/Cloudinary";
import { ConditionalWrapper } from "../../../../Atoms/ConditionalWrapper";
import ReadMore from "../../../../Atoms/ReadMore";
import { Text } from "../../../../Atoms/Typography";
import styles from "./CreatorExcerpt.module.css";
import ProfileHeads from "./ProfileHeads";
import { XLogo } from "./xLogo";

type FeaturedCommentProps = {
  text: string;
  canExpand?: boolean;
};
function FeaturedComment({ text, canExpand }: FeaturedCommentProps) {
  const tracker = useAnalyticsTracker();
  const lines = text.split("\n");

  function handleExpand() {
    tracker.trackEvent({
      category: "detail",
      action: "expand",
      label: "owner-comment",
    });
  }

  return (
    <ConditionalWrapper
      condition={canExpand}
      wrapper={(children) => (
        <ReadMore onExpand={handleExpand}>{children}</ReadMore>
      )}
    >
      <p>
        {lines.map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
      </p>
    </ConditionalWrapper>
  );
}

export type CreatorExcerptProps = {
  data: { product: GetProductQuery["product"]; guides?: Guide[] };
  canExpand?: boolean;
};

export default function CreatorExcerpt({
  data,
  canExpand,
}: CreatorExcerptProps) {
  const { product, guides } = data;
  const { featuredGuide } = product;
  const { featuredGuideComment } = product.translation;
  const tracker = useAnalyticsTracker();
  const firstThreeGuides: any =
    guides && guides.length > 0
      ? guides.filter((g) => !!g.profilePic).slice(0, 3)
      : [];

  const links = [
    {
      href: product.influencer?.instagramUrl,
      icon: <FaInstagram size={30} color="#FFF" />,
      label: "Instagram",
      color: "#E4405F",
    },
    {
      href: product.influencer?.youtubeUrl,
      icon: <FaYoutube size={30} color="#FFF" />,
      label: "YouTube",
      color: "#FF0000",
    },
    {
      href: product.influencer?.tiktokUrl,
      icon: <FaTiktok size={30} color="#FFF" />,
      label: "TikTok",
      color: "#010101",
    },
    {
      href: product.influencer?.xUrl,
      icon: <XLogo size={30} color="#FFF" />,
      label: "X (Twitter)",
      color: "#000000",
    },
  ];

  const guideRecommendationText = (
    <>
      <div>
        <Text size="large" bold>
          Why{" "}
          {featuredGuide.isActive
            ? featuredGuide.nickName + " Recommends"
            : "Our Guides Recommend"}{" "}
          This Tour
        </Text>
      </div>
      <p>
        <Text size="smaller">
          Tips and recommendations by our official guides
        </Text>
      </p>
    </>
  );

  const shouldShowProfile =
    product.influencer || featuredGuide.isActive === true;

  return (
    <div>
      <div className={styles.ProductCreatorAndGuides}>
        <div style={{ paddingBottom: 20 }}>
          {product.influencer ? (
            <Fragment>
              <div>
                <Text size="large" bold>
                  {product.willInfluencerGuide
                    ? "Designed & Guided by "
                    : "Designed by"}
                  {product.influencer.name}
                </Text>
              </div>
            </Fragment>
          ) : (
            <Fragment>{guideRecommendationText}</Fragment>
          )}
        </div>

        <div>
          <FeaturedComment text={featuredGuideComment} canExpand={canExpand} />

          <div className={styles.DetailPartsOwnerPeople}>
            {shouldShowProfile && (
              <div className={styles.DetailPartsOwnerProfile}>
                <Link
                  title={`${featuredGuide.nickName} profile page`}
                  href={`/guide-profile/${featuredGuide.id}?ref=detail-owner`}
                  className={styles.HeadContainer}
                >
                  {featuredGuide.profilePic && (
                    <CloudinaryImage
                      transformations={["w_64", "h_64", "c_fill"]}
                      src={
                        product.influencer
                          ? product.influencer.iconSrc
                          : featuredGuide.profilePic
                      }
                    />
                  )}
                </Link>
                <div className={styles.DetailPartsOwnerProfileText}>
                  <Text size="normal" bold>
                    {product.influencer
                      ? product.influencer.name
                      : featuredGuide.nickName}
                  </Text>
                  <p>{product.influencer ? "Influencer" : "Official guide"}</p>
                </div>
              </div>
            )}

            <div className={styles.DetailPartsOwnerMoreGuides}>
              {guides && guides.length > 0 ? (
                <ProfileHeads guides={firstThreeGuides} />
              ) : null}

              {product.influencer ? (
                <div style={{ display: "flex", gap: 10, marginTop: 15 }}>
                  {links
                    .filter((link) => !!link.href)
                    .map((link) => (
                      <a
                        key={link.label}
                        href={link.href!}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={link.label}
                        className={styles.InfluencerSocials}
                        style={{
                          backgroundColor: link.color,
                        }}
                      >
                        {link.icon}
                      </a>
                    ))}
                </div>
              ) : (
                <span style={{ marginLeft: 16 }}>
                  <Link
                    href={`/guide-listing/${product.id}`}
                    target="_blank"
                    onClick={() =>
                      tracker.trackEvent({
                        category: "onpage",
                        action: "click-view-all-guides",
                        label: "detail::owner::view-all-guides",
                      })
                    }
                    className="ui-Link"
                  >
                    View all guides
                  </Link>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
